<script setup>
import { isEqual, sortBy } from 'lodash-es';
import { computed, nextTick, reactive, watch } from 'vue';
import HawkText from '~/common/components/atoms/hawk-text.vue';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';
import CellNotesPopup from '~/terra/components/barcode-scan-map/cell-notes-popup.vue';
import FeatureActionsToolbar from '~/terra/components/feature-details/actions-toolbar/feature-actions-toolbar.vue';
import FeatureDetailsSlider from '~/terra/components/feature-details/feature-details-slider.vue';
import { createSerialNumberImage, useBarcodeScanValidationHelper } from '~/terra/composables/barcode-scan-helper.composable';
import { useTerraStore } from '~/terra/store/terra.store';
import { useFeatureActions } from '~/terra/utils/feature-actions.composable.js';

const props = defineProps({
  sidebarState: {
    type: Object,
    required: true,
  },
});
const emit = defineEmits(['openCreationPopup']);
const { $t, $services, auth_store, route } = useCommonImports();
const $form_create_permission = inject('$form_create_permission');
const terra_store = useTerraStore();
const { handleClearSerialNumbers, handleEditFeature, handleCopy, moveSelectedFeatures, deleteSelectedFeatures, handleExportSerialNumbers, handleInverseSerialNumbers } = useFeatureActions();
const { parseIndicatorConfig } = useBarcodeScanValidationHelper();

const state = reactive({
  selected_cells: [],
  cell_notes_map: {},
  notes_popup: null,
});
const footer_container = ref(null);
const { width: containerWidth } = useElementSize(footer_container);

const available_toolbar_width = computed(() => {
  const availableWidth = containerWidth.value;
  return availableWidth;
});

const footer_style = computed(() => {
  return `${
    props.sidebarState?.active_menu?.length
      ? `width:calc(100vw - 380px); left:380px;`
      : `width:calc(100vw - 80px); left:80px;`
  }`;
});
const features_list = computed(() => sortBy(terra_store.features_on_map, ['properties.name']));
const feature_current_index = computed(() => {
  const selected_feature = terra_store.selected_features[0];
  if (!selected_feature)
    return -1;
  return features_list.value.findIndex(feature => feature.properties.uid === selected_feature.properties.uid);
});
const is_bulk_selected = computed(() => {
  return terra_store.selected_features.length > 1;
});

const selected_feature = computed(() => {
  return terra_store.features_hash[terra_store?.selected_features?.[0]?.properties?.uid];
});

const feature_layout_details = computed(() => {
  const { extraProperties } = selected_feature.value.properties;
  const num_nodules_horizontal = Number.parseInt(extraProperties?.num_modules_horizontal);
  const num_modules_vertical = Number.parseInt(extraProperties?.num_modules_vertical);

  return {
    num_nodules_horizontal,
    num_modules_vertical,
    is_horizontal_layout: num_nodules_horizontal < num_modules_vertical,
    is_valid_data: !Number.isNaN(num_nodules_horizontal) && !Number.isNaN(num_modules_vertical),
  };
});

const slider_styles = computed(() => {
  const feature_layout = feature_layout_details.value;
  if (feature_layout.is_horizontal_layout) {
    return `height:calc(100vh - 65px);`;
  }
  else {
    return footer_style.value;
  }
});

const slider_classes = computed(() => {
  const feature_layout = feature_layout_details.value;
  if (feature_layout.is_horizontal_layout) {
    return 'right-0 w-[400px]';
  }
  else {
    return 'bottom-0 h-[320px]';
  }
});

const selection_tools = computed(() => {
  return [
    {
      uid: 'select_all',
      label: $t('Select all'),
      on_click: () => {
        state.selected_cells = getSerialNumberKeysWithValues();
      },
    },
    {
      uid: 'invert_selection',
      label: $t('Invert selection'),
      on_click: () => {
        state.selected_cells = getSerialNumberKeysWithValues().filter(item => !state.selected_cells.includes(item));
      },
    },
  ];
});

const hawk_menu_items = computed(() => {
  const permissions = {
    disabled: !terra_store.check_permission('modify_features'),
    disabled_reason: $t('You don\'t have permissions to perform this action.'),
  };
  const feature_items = [
    {
      uid: 'edit',
      label: $t('Edit'),
      on_click: handleEditFeature,
    },
    {
      uid: 'copy',
      label: $t('Copy'),
      on_click: handleCopy,
    },
    {
      uid: 'move',
      label: $t('Move'),
      on_click: moveSelectedFeatures,
    },
    {
      uid: 'delete',
      label: $t('Delete'),
      on_click: deleteSelectedFeatures,
    },
  ].map((item) => {
    return {
      ...item,
      ...permissions,
    };
  });
  return [
    ...(auth_store.has_tasks_access
      ? [{
          uid: 'create-task',
          label: $t('Create task'),
          disabled: !auth_store.check_permission('create_tasks', route.params.asset_id),
          disabled_reason: $t('You don\'t have permissions to perform this action.'),
          on_click: () => emit('openCreationPopup', 'task'),
        }]
      : []),
    ...(!auth_store.has_forms_access || $form_create_permission?.value?.state === 'hidden'
      ? []
      : [{
          uid: 'create-form',
          label: $t('Create form'),
          disabled: $form_create_permission?.value?.state === 'disabled',
          disabled_reason: $form_create_permission?.value?.tooltip,
          on_click: () => emit('openCreationPopup', 'form'),
        }]),
    ...feature_items,
  ];
});
async function handleExport() {
  try {
    // Fetch notes for all selected features
    const notes_promises = terra_store.selected_features.map(async (feature) => {
      const feature_uid = feature.properties.uid;
      const project_uid = feature.properties.project;
      try {
        const { data } = await $services.terra.post({
          url: `barcode-scans/container/${terra_store.container.uid}/project/${project_uid}/metadata`,
          baseURL: import.meta.env.VITE_APP_API_HOST,
          body: { feature_uids: [feature_uid] },
        });
        return { feature_uid, notes_map: data?.[feature_uid] || {} };
      }
      catch (err) {
        logger.error(err);
        return { feature_uid, notes_map: {} };
      }
    });

    const notes_results = await Promise.all(notes_promises);
    const notes_map_by_feature = notes_results.reduce((acc, { feature_uid, notes_map }) => {
      acc[feature_uid] = notes_map;
      return acc;
    }, {});

    await handleExportSerialNumbers(notes_map_by_feature);
  }
  catch (err) {
    logger.error(err);
  }
}

const actions_menu_items = computed(() => {
  const permission = terra_store.check_permission('modify_feature_properties');

  // When cells are selected, show only add note and clear options
  if (state.selected_cells.length > 0) {
    return [
      {
        uid: 'add-note',
        label: 'Add note',
        disabled: !permission,
        tooltip: permission ? '' : $t('You don\'t have permissions to perform this action.'),
        on_click: () => openCellNotesPopup([...state.selected_cells]),
      },
      {
        uid: 'clear_selected',
        label: $t('Clear'),
        disabled: !permission,
        tooltip: permission ? '' : $t('You don\'t have permissions to perform this action.'),
        on_click: () => {
          const project = selected_feature.value.properties.project;
          const feature_uid = selected_feature.value.properties.uid;
          const payload = {
            [project]: {
              [feature_uid]: state.selected_cells,
            },
          };
          handleClearSerialNumbers({
            clear_selected: true,
            payload,
            on_submit: () => {
              state.selected_cells = [];
            },
          });
        },
      },
    ];
  }

  // When no cells selected, show all options
  return [
    {
      uid: 'export-excel',
      label: $t('Export as excel'),
      on_click: handleExport,
    },
    {
      uid: 'download-image',
      label: $t('Download image'),
      on_click: () => {
        createSerialNumberImage(selected_feature.value.properties.name || 'Untitled');
      },
    },
    {
      uid: 'inverse-serial-numbers',
      label: $t('Inverse serial numbers'),
      disabled: !terra_store.check_permission('modify_scan_data'),
      tooltip: !terra_store.check_permission('modify_scan_data') && $t('You don\'t have permissions to perform this action.'),
      on_click: handleInverseSerialNumbers,
    },
    {
      uid: 'clear_all',
      label: $t('Clear all'),
      disabled: !permission,
      tooltip: permission ? '' : $t('You don\'t have permissions to perform this action.'),
      on_click: handleClearSerialNumbers,
    },
  ];
});

const get_barcode_grid_data = computed(() => {
  const feature_layout = feature_layout_details.value;
  if (!feature_layout.is_valid_data)
    return null;

  const duplicate_serial_number_key_map = terra_store.feature_duplicates_map?.[selected_feature.value?.properties?.uid] || {};
  const { extraProperties } = selected_feature.value.properties;
  let disabled_modules = extraProperties?.disabled_modules || [];
  let indicator_modules = extraProperties?.indicator_modules || [];
  let indicator_config = extraProperties?.indicator_config || {};
  if (!Array.isArray(disabled_modules)) {
    disabled_modules = disabled_modules.slice(1, -1).split(',').map(v => v.trim());
  }
  if (!Array.isArray(indicator_modules)) {
    indicator_modules = indicator_modules.slice(1, -1).split(',').map(v => v.trim());
  }
  if (typeof indicator_config === 'string') {
    indicator_config = parseIndicatorConfig(indicator_config).value;
  }
  const { indicator_rows, indicator_cols } = getIndicatorDetails(indicator_config);
  const matrix = [];

  const header_row = [{ is_header: true, value: '', class: 'w-10 h-8' }];
  for (let i = 1; i <= feature_layout.num_nodules_horizontal; i++) {
    header_row.push({ is_header: true, value: i, is_column_header: true, class: 'w-[150px] h-8' });
    if (indicator_cols.includes(i)) {
      header_row.push({ is_header: true, value: '', class: 'w-8 h-6' });
    }
  }
  matrix.push(header_row);

  let row_start = Number(extraProperties?.row_start ?? 0);
  let column_start = Number(extraProperties?.col_start ?? 0);
  if (row_start !== 0) {
    row_start--;
  }
  if (column_start !== 0) {
    column_start--;
  }

  for (let i = row_start; i < row_start + feature_layout.num_modules_vertical; i++) {
    // Add indicator row spacer after this row if needed
    let indicator_data_row = [];
    if (indicator_rows.includes(i + 1)) {
      indicator_data_row = [{ is_header: true, value: '', class: 'w-10 h-6' }];
    }

    const data_row = [{ is_header: true, value: i + 1, class: 'w-10 h-[90px]' }];
    for (let j = column_start; j < column_start + feature_layout.num_nodules_horizontal; j++) {
      const serial_number = extraProperties?._serial_numbers?.[`${i}:${j}`] || '';
      const module_key = `${i + 1}:${j + 1}`;

      data_row.push({
        is_header: false,
        row: i,
        column: j,
        serial_number_key: `${i}:${j}`,
        value: serial_number,
        is_duplicate: !!duplicate_serial_number_key_map[`${i}:${j}`],
        is_disabled: disabled_modules?.includes(module_key) ?? false,
        is_indicator: indicator_modules?.includes(module_key) ?? false,
        indicator_config: indicator_config[module_key] || {},
      });

      if (indicator_cols.includes(j + 1)) {
        const position_key = `${i + 1}:${j + 1}-${i + 1}:${j + 2}`;
        data_row.push({
          is_col_indicator: true,
          class: 'w-[20px] h-[90px] text-xs rounded',
          indicator_config: indicator_config[position_key],
        });
      }

      if (indicator_rows.includes(i + 1)) {
        const position_key = `${i + 1}:${j + 1}-${i + 2}:${j + 1}`;
        indicator_data_row.push({
          is_row_indicator: true,
          class: 'w-[150px] h-6 text-xs rounded',
          indicator_config: indicator_config[position_key],
        });
        if (indicator_cols.includes(j + 1)) {
          indicator_data_row.push({ is_header: true, value: '', class: 'w-[20px] h-6' });
        }
      }
    }
    matrix.push(data_row);
    if (indicator_data_row.length) {
      matrix.push(indicator_data_row);
    }
  }

  return matrix;
});

const last_scanned_date = computed(() => selected_feature.value.properties?.extraProperties?._serial_numbers_last_updated_at);

const highlighted_serial_keys = computed(() => {
  const term = (terra_store.searched_serial_number || '').toLowerCase();
  if (!term || !get_barcode_grid_data.value)
    return [];
  return get_barcode_grid_data.value
    .flat()
    .filter(item => item.serial_number_key && item.value && String(item.value).toLowerCase().includes(term))
    .map(item => item.serial_number_key);
});

function getIndicatorDetails(indicator_config) {
  const in_between_positions = Object.keys(indicator_config).filter(v => v.includes('-'));
  const indicator_rows = new Set();
  const indicator_cols = new Set();

  in_between_positions.forEach((item) => {
    const [start, end] = item.split('-');
    const [r1, c1] = start.split(':').map(Number);
    const [r2, c2] = end.split(':').map(Number);

    if (r1 === r2) { // column range (vertical indicator between columns)
      indicator_cols.add(c1);
    }
    else if (c1 === c2) { // row range (horizontal indicator between rows)
      indicator_rows.add(r1);
    }
  });
  return {
    indicator_rows: [...indicator_rows],
    indicator_cols: [...indicator_cols],
  };
}

function handleSelectCell(cell) {
  terra_store.searched_serial_number = '';
  if (state.selected_cells.includes(cell.serial_number_key)) {
    state.selected_cells = state.selected_cells.filter(item => item !== cell.serial_number_key);
  }
  else {
    state.selected_cells.push(cell.serial_number_key);
  }
}

const notes_url = computed(() => {
  const container_view_uid = terra_store.container?.uid;
  const project_uid = selected_feature.value?.properties?.project;
  return `barcode-scans/container/${container_view_uid}/project/${project_uid}/metadata`;
});

async function fetchCellNotes() {
  try {
    const feature_uid = selected_feature.value?.properties?.uid;
    const project_uid = selected_feature.value?.properties?.project;
    if (!feature_uid || !project_uid || !terra_store.container?.uid) {
      state.cell_notes_map = {};
      return;
    }
    const { data } = await $services.terra.post({
      url: notes_url.value,
      baseURL: import.meta.env.VITE_APP_API_HOST,
      body: { feature_uids: [feature_uid] },
    });
    state.cell_notes_map = data?.[feature_uid] || {};
  }
  catch (err) {
    logger.error(err);
    state.cell_notes_map = {};
  }
}

function getCellNoteCount(cell_key) {
  if (!cell_key)
    return 0;
  const entry = state.cell_notes_map?.[cell_key];
  return (entry?.notes?.length || 0) + (entry?.attachments?.length || 0);
}

function canAddNoteToCell(cell_key) {
  if (!cell_key)
    return false;
  return cell_key in state.cell_notes_map;
}

function openCellNotesPopup(cell_keys) {
  const feature_uid = selected_feature.value?.properties?.uid;
  const project_uid = selected_feature.value?.properties?.project;
  const container_view_uid = terra_store.container?.uid;
  if (!feature_uid || !project_uid || !container_view_uid || !cell_keys?.length)
    return;
  // Filter to only cells that exist in the map data
  const valid_cells = cell_keys.filter(key => canAddNoteToCell(key));
  if (!valid_cells.length)
    return;
  state.notes_popup = { cell_keys: valid_cells };
}

function closeCellNotesPopup() {
  state.notes_popup = null;
}
function getSerialNumberKeysWithValues() {
  return get_barcode_grid_data.value
    .flat()
    .filter(item => item.serial_number_key && item.value)
    .map(item => item.serial_number_key);
}
function handleNavigation(direction) {
  terra_store.searched_serial_number = '';
  const current_index = feature_current_index.value;
  const next_index = direction === 'next' ? current_index + 1 : current_index - 1;
  if (next_index >= 0 && next_index < features_list.value.length) {
    const next_feature = features_list.value[next_index];
    terra_store.selected_features = [next_feature];
  }
}

watch(() => terra_store.selected_features.map(f => f.properties.uid), (new_val, old_val) => {
  if (!isEqual(new_val, old_val)) {
    state.selected_cells = [];
    state.cell_notes_map = {};
    closeCellNotesPopup();
    fetchCellNotes();
  }
}, { immediate: true });

watch(() => state.selected_cells.length, (new_length) => {
  // Close popup when user starts selecting cells (going from 0 to 1+ selections)
  if (new_length > 0 && state.notes_popup) {
    closeCellNotesPopup();
  }
});

watch([highlighted_serial_keys, () => selected_feature.value?.properties?.uid], async () => {
  if (!highlighted_serial_keys.value.length)
    return;
  await nextTick();
  const container = document.getElementById('serial_numbers_container');
  const cell_el = container?.querySelector(`[data-serial-key="${highlighted_serial_keys.value[0]}"]`);
  cell_el?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
}, { immediate: true });
</script>

<template>
  <div
    v-if="is_bulk_selected"
    ref="footer_container"
    :style="footer_style"
    class="fixed z-[99] bottom-3 flex justify-center items-center space-x-4 w-auto"
  >
    <FeatureActionsToolbar
      class="mb-0.5"
      :available-width="available_toolbar_width"
      @open-creation-popup="emit('openCreationPopup', $event)"
    />
  </div>
  <div
    v-else-if="terra_store.selected_features.length === 1"
    :style="slider_styles"
    class="fixed z-[99] bg-white border"
    :class="slider_classes"
  >
    <div class="flex items-center flex-wrap justify-between px-4 py-2 border-b border-gray-300">
      <div class="flex items-center flex-wrap gap-3">
        <div id="feature_name_title" class="text-md font-semibold">
          {{ selected_feature.properties.name }}
        </div>
        <FeatureDetailsSlider
          v-if="!state.selected_cells.length"
          :feature="selected_feature"
          :hawk_menu_items="hawk_menu_items"
        >
          <template #trigger="{ action }">
            <HawkButton
              type="link" class="text-sm"
              @click="action()"
            >
              {{ $t('Details') }} <IconHawkArrowRight class="-ml-0.5 w-5 h-5" />
            </HawkButton>
          </template>
        </FeatureDetailsSlider>
      </div>
      <div class="flex items-center flex-wrap gap-2">
        <div v-if="!feature_layout_details.is_horizontal_layout && !state.selected_cells.length" class="flex flex-row">
          <HawkButton type="text" :disabled="feature_current_index === 0" @click="handleNavigation('prev')">
            <IconHawkChevronLeft />{{ $t('Previous') }}
          </HawkButton>
          <HawkButton type="text" :disabled="feature_current_index === features_list.length - 1" @click="handleNavigation('next')">
            {{ $t('Next') }}<IconHawkChevronRight />
          </HawkButton>
        </div>
        <HawkMenu v-if="state.selected_cells.length" position="fixed" additional_trigger_classes="!ring-0 !border-0" :items="selection_tools">
          <template #trigger="{ open }">
            <div class="flex items-center border-2 border-gray-200 rounded-lg">
              <div class="text-xs font-medium text-gray-600 py-1.5 px-3" :class="{ 'bg-gray-100': open }">
                {{ state.selected_cells.length }} {{ $t('selected') }}
              </div>
              <div class="cursor-pointer border-l-2 border-gray-200 py-1.5 px-2" @click="state.selected_cells = []">
                <IconHawkXClose class="w-4 h-4" />
              </div>
            </div>
          </template>
        </HawkMenu>

        <HawkMenu
          :items="actions_menu_items"
          position="fixed"
          :has_bordered_trigger="false"
          additional_trigger_classes="!ring-0 p-0"
        />
        <HawkButton type="plain" size="sm" icon @click="terra_store.selected_features = []; terra_store.searched_serial_number = ''">
          <IconHawkXClose class="w-5 h-5 text-gray-600" />
        </HawkButton>
      </div>
    </div>
    <template v-if="get_barcode_grid_data">
      <div id="serial_numbers_container" class="overflow-auto p-4" :class="feature_layout_details.is_horizontal_layout ? 'h-[calc(100%-140px)]' : 'w-full h-[calc(100%-100px)]'">
        <div class="flex flex-col gap-2 w-max relative">
          <div
            class="absolute bg-gray-100 rounded-lg w-auto top-[30px] left-[36px] bottom-[-14px] right-[-12px] z-0"
          />
          <div
            v-for="(row, rowIndex) in get_barcode_grid_data"
            :key="`row-${rowIndex}`"
            class="flex gap-2"
          >
            <div
              v-for="(cell, colIndex) in row"
              :key="`cell-${rowIndex}-${colIndex}`"
            >
              <div
                v-if="cell.is_header"
                class="flex items-center justify-center"
                :class="cell.class"
              >
                <span class="text-xs font-semibold">
                  {{ cell.value }}
                </span>
              </div>
              <div
                v-else-if="cell.is_col_indicator || cell.is_row_indicator"
                class="flex items-center justify-center relative"
                :class="cell.class"
                :style="{ backgroundColor: cell.indicator_config?.backgroundColor || 'transparent' }"
              >
                <div class="text-white font-medium" :class="{ '-rotate-90': cell.is_col_indicator }">
                  {{ cell.indicator_config?.text }}
                </div>
              </div>
              <div
                v-else
                :data-serial-key="cell.serial_number_key"
                class="group/cell flex items-center justify-center border-2 relative rounded p-2 w-[150px] h-[90px]"
                :class="[
                  cell.class,
                  cell.is_disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                  (cell.is_disabled ? 'bg-gray-100 border-gray-400' : (cell.value ? 'bg-success-100 border-success-200' : 'bg-error-100 border-error-200')),
                  state.selected_cells.includes(cell.serial_number_key) ? '!border-primary-600' : '',
                  highlighted_serial_keys.includes(cell.serial_number_key) ? '!border-dotted !border-primary-600' : '',
                ]"
                @click="handleSelectCell(cell)"
              >
                <span class="break-all text-xs font-medium">
                  {{ cell.is_disabled ? 'No module' : cell.value }}
                </span>
                <IconHawkAlertTriangle v-if="cell.is_duplicate" class="absolute bottom-1 right-1 w-3 h-3 text-warning-500" />
                <div
                  v-if="cell.is_indicator"
                  v-tippy="{ content: $t('Indicator'), placement: 'right' }"
                  class="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-gray-400"
                />
                <div
                  v-if="cell.indicator_config?.text" class="absolute top-0.5 right-0.5 p-1 rounded max-w-[80px] flex items-center justify-center"
                  :style="{ backgroundColor: cell.indicator_config?.backgroundColor || '#eeeeee' }"
                >
                  <div class="text-white text-xs font-medium">
                    {{ cell.indicator_config.text }}
                  </div>
                </div>

                <button
                  v-if="canAddNoteToCell(cell.serial_number_key)"
                  v-tippy="getCellNoteCount(cell.serial_number_key) ? $t('View notes') : $t('Add note')"
                  type="button"
                  class="absolute top-1 left-1 items-center justify-center w-5 h-5 rounded bg-white shadow-sm hover:bg-gray-100 text-[10px] font-medium text-gray-700"
                  :class="getCellNoteCount(cell.serial_number_key) ? 'flex' : 'hidden group-hover/cell:flex'"
                  @click.stop="openCellNotesPopup([cell.serial_number_key])"
                >
                  <IconHawkMessageDotsCircle class="w-3 h-3 text-gray-600" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="feature_layout_details.is_horizontal_layout" class="flex items-center mt-2 justify-center">
        <div class="flex flex-row">
          <HawkButton type="text" :disabled="feature_current_index === 0" @click="handleNavigation('prev')">
            <IconHawkChevronLeft />{{ $t('Previous') }}
          </HawkButton>
          <HawkButton type="text" :disabled="feature_current_index === features_list.length - 1" @click="handleNavigation('next')">
            {{ $t('Next') }}<IconHawkChevronRight />
          </HawkButton>
        </div>
      </div>
      <div id="feature_last_scanned_date" class="text-xs text-gray-600 pl-7 pt-4">
        {{ $t('Last updated') }}:
        <span class="text-xs font-medium text-gray-900 pl-1">{{ last_scanned_date ? $date(last_scanned_date, 'D MMMM YYYY, h:mm a') : '--' }}</span>
      </div>
    </template>
    <div v-else class="p-4 text-center text-gray-500">
      {{ $t('Not configured for Barcode scan') }}
    </div>
  </div>
  <CellNotesPopup
    v-if="state.notes_popup && selected_feature"
    :cell_keys="state.notes_popup.cell_keys"
    :feature_uid="selected_feature.properties.uid"
    :project_uid="selected_feature.properties.project"
    :container_view_uid="terra_store.container?.uid"
    :feature_type="selected_feature.properties.featureType || ''"
    :can_edit="auth_store.check_permission('modify_terra_features', route.params.asset_id) || auth_store.check_permission('modify_terra_features_properties', route.params.asset_id)"
    :cell_notes_map="state.cell_notes_map"
    :on-updated="fetchCellNotes"
    @close="closeCellNotesPopup"
  />
</template>
